/**
 * TenantContextEditor Component
 * Two-panel layout: hierarchy tree on the left, context editor on the right.
 * Supports system prompt, data bundles, files, editable context prompt, and AgentPreview.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { activeLocale } from '@/utils/locale';
import { Modal, message } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import { useFeature } from '@/hooks/useFeature';
import { UploadArea, FilePreviewModal } from '@/components/common';
import { VaultAttachments } from '@modules/vault/components/VaultAttachments';
import { AgentPreview } from '@/components/common/AgentPreview';
import { ContextPromptViewer } from '@/components/common/ContextPromptViewer';
import { UrlResearchSection } from '@modules/data-bundles/components/UrlResearchSection';
import { CloudFilePicker } from '@modules/data-bundles/components/CloudFilePicker';
import { DataBundleApi } from '@modules/data-bundles/services/dataBundleService';
import { KNOWLEDGE_ACCEPT } from '@/constants/knowledgeFileTypes';
import '@modules/chatbots/components/BotContextFiles.css';
import '@modules/chatbots/components/BotModal.css';
import type { HierarchyTreeNode } from '@/types/admin';
import { groupAdminService } from '../services/groupAdminService';
import type { TenantContextFile, TenantContextPreviewFile, TenantContextPreviewResponse, TenantDataBundle, TenantDataBundlesResponse } from '../services/groupAdminService';
import './TenantContextEditor.css';
import '@modules/admin/pages/AdminPage.css';

// Level-based styling for tree nodes — M² brand palette (same as AdminPage)
const levelStyles = [
  { color: '#3DA8D4', bgColor: 'rgba(61, 168, 212, 0.12)', icon: 'crown' },
  { color: '#6CC7EF', bgColor: 'rgba(108, 199, 239, 0.12)', icon: 'building' },
  { color: '#8BBF2A', bgColor: 'rgba(139, 191, 42, 0.12)', icon: 'folder' },
  { color: '#FAB600', bgColor: 'rgba(250, 182, 0, 0.12)', icon: 'users' },
  { color: '#D50032', bgColor: 'rgba(213, 0, 50, 0.10)', icon: 'user' },
];

function getLevelIcon(level: number, color: string) {
  const icons: Record<string, JSX.Element> = {
    crown: (
      <svg viewBox="0 0 24 24" width="14" height="14" style={{ marginInlineEnd: '4px', color }}>
        <path d="M2 20h20v2H2zm2-2L1 7l5 3 6-8 6 8 5-3-3 13H4z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      </svg>
    ),
    building: (
      <svg viewBox="0 0 24 24" width="14" height="14" style={{ marginInlineEnd: '4px', color }}>
        <path d="M3 21h18M9 8h1M9 12h1M9 16h1M14 8h1M14 12h1M14 16h1M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    folder: (
      <svg viewBox="0 0 24 24" width="14" height="14" style={{ marginInlineEnd: '4px', color }}>
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    users: (
      <svg viewBox="0 0 24 24" width="14" height="14" style={{ marginInlineEnd: '4px', color }}>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="9" cy="7" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    user: (
      <svg viewBox="0 0 24 24" width="14" height="14" style={{ marginInlineEnd: '4px', color }}>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="12" cy="7" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
  };
  const style = levelStyles[Math.min(level, levelStyles.length - 1)];
  return icons[style.icon] || icons.user;
}

// Recursive tree node component
function TreeNode({
  node,
  onSelect,
  selectedTenantId,
  level = 0,
  collapsedNodes,
  onToggleCollapse,
}: {
  node: HierarchyTreeNode;
  onSelect: (tenantId: string, tenantName: string) => void;
  selectedTenantId: string | null;
  level?: number;
  collapsedNodes: Set<string>;
  onToggleCollapse: (tenantId: string) => void;
}) {
  const { t } = useLanguage();
  const isSelected = selectedTenantId === node.tenant_id;
  const style = levelStyles[Math.min(level, levelStyles.length - 1)];
  const hasChildren = node.has_children ?? (node.children && node.children.length > 0);
  const isCollapsed = collapsedNodes.has(node.tenant_id);

  return (
    <div className="tree-node">
      <div className="tree-node-collapsible">
        {hasChildren && (
          <div
            className="tree-node-toggle"
            onClick={(e) => { e.stopPropagation(); onToggleCollapse(node.tenant_id); }}
            title={isCollapsed ? t('common.expand') : t('common.collapse')}
          >
            {isCollapsed ? '▶' : '▼'}
          </div>
        )}
        {!hasChildren && <div style={{ width: 20 }} />}

        <div
          className={`tree-node-content tree-node-level-${Math.min(level, 4)} ${isSelected ? 'tree-node-selected' : ''}`}
          onClick={() => onSelect(node.tenant_id, node.tenant_name)}
          style={{ cursor: 'pointer', borderInlineStartColor: style.color, flex: 1 }}
        >
          {getLevelIcon(level, style.color)}
          <span className="tree-node-name" dir="auto">{node.tenant_name}</span>
          {node.user_count !== undefined && (
            <span className="tree-node-users" style={{ background: style.bgColor, color: style.color }}>
              <svg viewBox="0 0 24 24" width="12" height="12" style={{ marginInlineEnd: '3px' }}>
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" fill="none" stroke="currentColor" strokeWidth="2" />
                <circle cx="12" cy="7" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
              </svg>
              {node.user_count}
            </span>
          )}
        </div>
      </div>
      {hasChildren && !isCollapsed && node.children && node.children.length > 0 && (
        <div className="tree-children">
          {node.children.map((child) => (
            <TreeNode
              key={child.tenant_id}
              node={child}
              onSelect={onSelect}
              selectedTenantId={selectedTenantId}
              level={level + 1}
              collapsedNodes={collapsedNodes}
              onToggleCollapse={onToggleCollapse}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface TenantOption {
  tenant_id: string;
  tenant_name: string;
  tenant_code?: string;
  depth?: number;
  parent_tenant_id?: string | null;
}

interface TenantContextEditorProps {
  tenants: TenantOption[];
  loading?: boolean;
  hierarchyTree?: HierarchyTreeNode | null;
  onGetContextPrompt: (tenantId: string) => Promise<{ tenant_id: string; tenant_name: string; context_prompt: string | null; files_context_prompt: string | null }>;
  onUpdateContextPrompt: (tenantId: string, contextPrompt: string) => Promise<void>;
  onPreviewContext: (tenantId: string) => Promise<TenantContextPreviewResponse>;
  onListFiles?: (tenantId: string) => Promise<{ files: TenantContextFile[] }>;
  onUploadFile?: (tenantId: string, file: File) => Promise<{ success: boolean; attachment: TenantContextFile }>;
  onDeleteFile?: (tenantId: string, attachmentId: string) => Promise<void>;
  onUpdateFile?: (tenantId: string, attachmentId: string, data: { include_as_context?: boolean }) => Promise<any>;
  onAddCloudFiles?: (tenantId: string, provider: string, fileIds: string[]) => Promise<any>;
  // Data bundles
  onListDataBundles?: (tenantId: string) => Promise<TenantDataBundlesResponse>;
  onUpdateDataBundles?: (tenantId: string, bundleIds: string[]) => Promise<TenantDataBundlesResponse>;
  onRemoveDataBundle?: (tenantId: string, bundleId: string) => Promise<void>;
  onUpdateDataBundle?: (tenantId: string, bundleId: string, data: { include_as_context: boolean }) => Promise<void>;
  // Files context prompt
  onUpdateFilesContextPrompt?: (tenantId: string, prompt: string) => Promise<void>;
  onRegenerateContext?: (tenantId: string) => Promise<{ success: boolean; message: string }>;
}

const TenantContextEditor: React.FC<TenantContextEditorProps> = ({
  loading: tenantsLoading,
  hierarchyTree,
  onGetContextPrompt,
  onUpdateContextPrompt,
  onPreviewContext,
  onListFiles,
  onUploadFile,
  onDeleteFile,
  onUpdateFile,
  onAddCloudFiles,
  onListDataBundles,
  onUpdateDataBundles,
  onRemoveDataBundle,
  onUpdateDataBundle: _onUpdateDataBundle,
  // Auto-file-sync handles regeneration in the background after every
  // mutation; `onRegenerateContext` is the manual override the user
  // can trigger from the AgentPreview refresh button (e.g. to force
  // a rebuild after a bundle was edited elsewhere). Update of the
  // editable text prompt is still unused here.
  onUpdateFilesContextPrompt: _onUpdateFilesContextPrompt,
  onRegenerateContext,
}) => {
  const { t } = useLanguage();
  const enabled = useFeature('tenant_context.editor');
  // Cloud picker honors the per-tenant "Cloud storage connections" switch, same
  // as User Settings and the data-bundle modal — hidden when it's off.
  const cloudEnabled = useFeature('integrations.cloud_storage');

  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [selectedTenantName, setSelectedTenantName] = useState<string>('');
  const [contextPrompt, setContextPrompt] = useState<string>('');
  const [originalPrompt, setOriginalPrompt] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewContent, setPreviewContent] = useState<string>('');
  const [previewFiles, setPreviewFiles] = useState<TenantContextPreviewFile[]>([]);
  const [previewFilesPrompt, setPreviewFilesPrompt] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [refreshingTenantChain, setRefreshingTenantChain] = useState(false);
  const [collapsedNodes, setCollapsedNodes] = useState<Set<string>>(new Set());

  // File state
  const [files, setFiles] = useState<TenantContextFile[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [cloudPickerVisible, setCloudPickerVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState<{ fileId?: string; fileName: string; fileType: string } | null>(null);

  // Data bundle state
  const [bundles, setBundles] = useState<TenantDataBundle[]>([]);
  const [bundleSelectorVisible, setBundleSelectorVisible] = useState(false);
  const [allAvailableBundles, setAllAvailableBundles] = useState<Array<{ data_bundle_id: string; bundle_name: string; bundle_description: string | null; file_count: number; created_by_name?: string; created_at?: string; is_shared?: boolean }>>([]);
  const [loadingAvailableBundles, setLoadingAvailableBundles] = useState(false);
  const [bundleSearchQuery, setBundleSearchQuery] = useState('');

  // Files context prompt — read-only, auto-syncs in background.
  const [filesContextPrompt, setFilesContextPrompt] = useState<string>('');

  const isDirty = contextPrompt !== originalPrompt;
  const hasFileSupport = Boolean(onListFiles && onUploadFile && onDeleteFile);
  // Library on => the legacy Data Bundles section retires from this editor;
  // organizational knowledge is linked from the Library instead.
  const libraryEnabled = useFeature('modules.library');
  const hasBundleSupport = Boolean(onListDataBundles) && !libraryEnabled;

  useEffect(() => {
    if (selectedTenantId) {
      loadContextPrompt(selectedTenantId);
      if (hasFileSupport) loadFiles(selectedTenantId);
      if (hasBundleSupport) loadBundles(selectedTenantId);
    }
  }, [selectedTenantId]);

  const toggleNodeCollapse = (tenantId: string) => {
    setCollapsedNodes(prev => {
      const next = new Set(prev);
      if (next.has(tenantId)) next.delete(tenantId);
      else next.add(tenantId);
      return next;
    });
  };

  const handleTenantSelect = (tenantId: string, tenantName: string) => {
    setSelectedTenantId(tenantId);
    setSelectedTenantName(tenantName);
  };

  const loadContextPrompt = async (tenantId: string) => {
    setLoading(true);
    try {
      const data = await onGetContextPrompt(tenantId);
      const prompt = data.context_prompt || '';
      setContextPrompt(prompt);
      setOriginalPrompt(prompt);
      const fcp = data.files_context_prompt || '';
      setFilesContextPrompt(fcp);
    } catch {
      message.error(t('groupAdmin.context.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const loadFiles = async (tenantId: string) => {
    if (!onListFiles) return;
    setFilesLoading(true);
    try {
      const data = await onListFiles(tenantId);
      setFiles(data.files || []);
    } catch {
      message.error(t('groupAdmin.context.files.loadError'));
    } finally {
      setFilesLoading(false);
    }
  };

  const loadBundles = async (tenantId: string) => {
    if (!onListDataBundles) return;
    try {
      const data = await onListDataBundles(tenantId);
      setBundles(data.bundles || []);
    } catch {
      // silent
    }
  };

  const handleSave = async () => {
    if (!selectedTenantId) return;
    setSaving(true);
    try {
      await onUpdateContextPrompt(selectedTenantId, contextPrompt);
      setOriginalPrompt(contextPrompt);
      message.success(t('groupAdmin.context.saved'));
    } catch {
      message.error(t('groupAdmin.context.saveError'));
    } finally {
      setSaving(false);
    }
  };

  // Manual save / regenerate of the files context prompt removed —
  // auto-file-sync now keeps it fresh in the background. The
  // onUpdateFilesContextPrompt / onRegenerateContext callbacks stay
  // on the props interface for backward compat with super-admin's
  // direct API usage but the UI no longer surfaces them.

  const handlePreview = async () => {
    if (!selectedTenantId) return;
    setPreviewVisible(true);
    setPreviewLoading(true);
    try {
      const data = await onPreviewContext(selectedTenantId);
      setPreviewContent(data.hierarchical_context || t('groupAdmin.context.noContext'));
      setPreviewFiles(data.files || []);
      setPreviewFilesPrompt(data.files_context_prompt || '');
    } catch {
      setPreviewContent(t('groupAdmin.context.previewError'));
      setPreviewFiles([]);
      setPreviewFilesPrompt('');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleFileUpload = useCallback(async (files: FileList | File[]) => {
    if (!selectedTenantId || !onUploadFile) return;
    setUploading(true);
    // Per-file: one failure must not cost the rest of the batch, and the
    // list refreshes with whatever DID land.
    let uploaded = 0;
    for (const file of Array.from(files)) {
      try {
        await onUploadFile(selectedTenantId, file);
        uploaded++;
      } catch (err: any) {
        message.error(err?.response?.data?.error || t('groupAdmin.context.files.uploadError'));
      }
    }
    if (uploaded > 0) {
      message.success(t('groupAdmin.context.files.uploaded'));
      loadFiles(selectedTenantId);
    }
    setUploading(false);
  }, [selectedTenantId, onUploadFile]);

  const handleCloudFilesSelected = useCallback(async (provider: string, fileIds: string[]) => {
    if (!selectedTenantId || !onAddCloudFiles) return;
    setCloudPickerVisible(false);
    setUploading(true);
    try {
      const result = await onAddCloudFiles(selectedTenantId, provider, fileIds);
      const addedCount = result.files?.length || 0;
      if (addedCount > 0) message.success(t('counts.cloudFilesAdded', { count: addedCount }));
      if (result.skipped?.length > 0) message.warning(t('dataBundles.filesSkippedCount', { count: result.skipped.length }));
      loadFiles(selectedTenantId);
    } catch (err: any) {
      message.error(err?.response?.data?.error || t('cloudStorage.failedToAddFiles'));
    } finally {
      setUploading(false);
    }
  }, [selectedTenantId, onAddCloudFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => { e.preventDefault(); setDragOver(true); }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => { e.preventDefault(); setDragOver(false); }, []);
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) handleFileUpload(e.dataTransfer.files);
  }, [handleFileUpload]);

  const handleToggleDirectMode = async (attachmentId: string, includeAsContext: boolean) => {
    if (!selectedTenantId || !onUpdateFile) return;
    try {
      await onUpdateFile(selectedTenantId, attachmentId, { include_as_context: includeAsContext });
      loadFiles(selectedTenantId);
    } catch {
      message.error(t('groupAdmin.context.files.updateError'));
    }
  };

  const handleDeleteFile = async (attachmentId: string) => {
    if (!selectedTenantId || !onDeleteFile) return;
    Modal.confirm({
      title: t('groupAdmin.context.files.deleteConfirm'),
      onOk: async () => {
        try {
          await onDeleteFile(selectedTenantId, attachmentId);
          message.success(t('groupAdmin.context.files.deleted'));
          loadFiles(selectedTenantId);
        } catch {
          message.error(t('groupAdmin.context.files.deleteError'));
        }
      },
    });
  };

  const openBundleSelector = async () => {
    setBundleSelectorVisible(true);
    setBundleSearchQuery('');
    if (allAvailableBundles.length === 0) {
      setLoadingAvailableBundles(true);
      try {
        const data = await DataBundleApi.listAvailableBundles();
        setAllAvailableBundles((data.data_bundles || []).map(b => ({
          data_bundle_id: b.data_bundle_id,
          bundle_name: b.bundle_name,
          bundle_description: b.bundle_description,
          file_count: b.file_count,
          created_by_name: b.created_by_name || undefined,
          created_at: b.created_at || undefined,
          is_shared: b.is_shared,
        })));
      } catch {
        // silent
      } finally {
        setLoadingAvailableBundles(false);
      }
    }
  };

  const filteredAvailableBundles = React.useMemo(() => {
    if (!bundleSearchQuery) return allAvailableBundles;
    const q = bundleSearchQuery.toLowerCase();
    return allAvailableBundles.filter(b =>
      b.bundle_name.toLowerCase().includes(q) ||
      (b.bundle_description || '').toLowerCase().includes(q) ||
      (b.created_by_name || '').toLowerCase().includes(q)
    );
  }, [allAvailableBundles, bundleSearchQuery]);

  const toggleBundleSelection = useCallback(async (bundleId: string) => {
    if (!selectedTenantId || !onUpdateDataBundles) return;
    const existingIds = bundles.map(b => b.data_bundle_id);
    const isLinked = existingIds.includes(bundleId);
    const newIds = isLinked
      ? existingIds.filter(id => id !== bundleId)
      : [...existingIds, bundleId];
    try {
      await onUpdateDataBundles(selectedTenantId, newIds);
      await loadBundles(selectedTenantId);
    } catch {
      message.error(t('dataBundles.updateBundlesFailed'));
    }
  }, [bundles, selectedTenantId, onUpdateDataBundles, loadBundles]);

  // handleAddBundles replaced by toggleBundleSelection (multi-select in modal)

  const handleRemoveBundle = async (bundleId: string) => {
    if (!selectedTenantId || !onRemoveDataBundle) return;
    try {
      await onRemoveDataBundle(selectedTenantId, bundleId);
      await loadBundles(selectedTenantId);
      message.success(t('dataBundles.bundleRemoved'));
    } catch {
      message.error(t('dataBundles.removeBundleFailed'));
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Build preview content string for AgentPreview
  const buildPreviewString = (): string => {
    const lines: string[] = [];

    if (previewContent && previewContent !== t('groupAdmin.context.noContext')) {
      lines.push(previewContent);
    }

    const directFiles = previewFiles.filter(f => f.include_as_context);
    if (directFiles.length > 0) {
      lines.push('\n## Organizational Reference Documents');
      for (const f of directFiles) {
        lines.push(`\n### ${f.file_name}\n\`\`\`\n[Full file content injected here — ${f.estimated_tokens?.toLocaleString() || '?'} tokens]\n\`\`\``);
      }
    }

    const toolFiles = previewFiles.filter(f => !f.include_as_context);
    if (toolFiles.length > 0 || previewFilesPrompt) {
      lines.push('\n## Organizational Knowledge');
      if (previewFilesPrompt) {
        lines.push(previewFilesPrompt);
      } else {
        for (const f of toolFiles) {
          const tokens = f.estimated_tokens ? ` (${f.estimated_tokens.toLocaleString()} tokens)` : '';
          lines.push(`- ${f.file_name}${tokens}`);
        }
      }
    }

    return lines.length > 0 ? lines.join('\n') : t('groupAdmin.context.noContext');
  };

  if (!enabled) return null;

  return (
    <div className="hierarchy-section">
      <div className="hierarchy-layout-fixed">
        {/* Left panel: Hierarchy Tree */}
        <div className="card hierarchy-card-fixed">
          <div className="card-header">
            <h3>{t('groupAdmin.context.title')}</h3>
          </div>
          <div className="card-content">
            {tenantsLoading ? (
              <div className="loading-cell" style={{ padding: '40px', textAlign: 'center' }}>
                {t('common.loading')}
              </div>
            ) : hierarchyTree ? (
              <div className="hierarchy-tree">
                <TreeNode
                  node={hierarchyTree}
                  onSelect={handleTenantSelect}
                  selectedTenantId={selectedTenantId}
                  collapsedNodes={collapsedNodes}
                  onToggleCollapse={toggleNodeCollapse}
                />
              </div>
            ) : (
              <div className="loading-cell" style={{ padding: '40px', textAlign: 'center' }}>
                {t('groupAdmin.context.selectTenantHint')}
              </div>
            )}
          </div>
        </div>

        {/* Right panel: Context Editor */}
        <div className="card tenant-users-card-fixed">
          <div className="card-header">
            <h3>{selectedTenantName ? `${t('groupAdmin.context.promptLabel')} — ${selectedTenantName}` : t('groupAdmin.context.promptLabel')}</h3>
            {selectedTenantId && (
              <div className="hierarchy-actions">
                <button
                  className="hierarchy-action-btn"
                  onClick={handlePreview}
                  title={t('groupAdmin.context.previewBtn')}
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  {t('groupAdmin.context.previewBtn')}
                </button>
                <button
                  className="hierarchy-action-btn hierarchy-action-refresh"
                  onClick={() => {
                    if (selectedTenantId) {
                      loadContextPrompt(selectedTenantId);
                      if (hasFileSupport) loadFiles(selectedTenantId);
                      if (hasBundleSupport) loadBundles(selectedTenantId);
                    }
                  }}
                  title={t('admin.refresh')}
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                    <path d="M21 3v5h-5" />
                    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                    <path d="M8 16l-5 5v-5h5" />
                  </svg>
                </button>
              </div>
            )}
          </div>
          <div className="card-content">
            {!selectedTenantId ? (
              <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
                <svg viewBox="0 0 24 24" width="48" height="48" style={{ color: 'var(--text-muted)', marginBottom: '16px' }} fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                </svg>
                <p style={{ color: 'var(--text-muted)' }}>{t('groupAdmin.context.selectTenantHint')}</p>
              </div>
            ) : loading ? (
              <div className="loading-cell" style={{ padding: '40px', textAlign: 'center' }}>
                {t('common.loading')}
              </div>
            ) : (
              <div className="tce-editor-content">
                {/* System Prompt Section */}
                <div className="data-bundle-section">
                  <label className="section-label">{t('groupAdmin.context.promptLabel')}</label>
                  <p className="section-description">{t('groupAdmin.context.description')}</p>
                  <textarea dir="auto"
                    className="context-textarea"
                    value={contextPrompt}
                    onChange={(e) => setContextPrompt(e.target.value)}
                    placeholder={t('groupAdmin.context.promptPlaceholder')}
                    rows={6}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                    <button
                      className="hierarchy-action-btn hierarchy-action-primary"
                      onClick={handleSave}
                      disabled={saving || !isDirty}
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                        <polyline points="17 21 17 13 7 13 7 21" />
                        <polyline points="7 3 7 8 15 8" />
                      </svg>
                      {saving ? t('common.saving') : t('common.save')}
                    </button>
                    {isDirty && (
                      <span className="tce-unsaved">{t('groupAdmin.context.unsavedChanges')}</span>
                    )}
                  </div>
                </div>

                {/* Data Bundles Section — matches BotModal data-bundles-section */}
                {hasBundleSupport && (
                  <div className="data-bundles-section">
                    <label className="section-label">{t('dataBundles.title')}</label>
                    <p className="section-description">
                      {t('groupAdmin.attachReusableDataBundlesTo')}
                    </p>

                    {bundles.length > 0 && (
                      <div className="bundle-selected-list">
                        {bundles.map((bundle) => (
                          <div key={bundle.data_bundle_id} className="bundle-selected-card">
                            <div className="bundle-selected-icon">
                              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                              </svg>
                            </div>
                            <div className="bundle-selected-info">
                              <div className="bundle-selected-name" dir="auto">{bundle.bundle_name}</div>
                              <div className="bundle-selected-meta">
                                {t('counts.files', { count: bundle.file_count })}
                                {bundle.bundle_context_prompt && <span style={{ color: 'var(--color-success)', marginInlineStart: '8px' }}>{t('groupAdmin.context.generated')}</span>}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="bundle-remove-btn"
                              onClick={() => handleRemoveBundle(bundle.data_bundle_id)}
                              title={t('bots.modal.removeDataBundle')}
                            >
                              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <button
                      type="button"
                      className="btn-add-bundle"
                      onClick={openBundleSelector}
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                      {bundles.length > 0 ? t('bots.addRemoveBundles') : t('bots.modal.selectDataBundles')}
                    </button>
                  </div>
                )}

                {/* Context Files Section */}
                {hasFileSupport && (
                  <div className="data-bundle-section bot-context-files">
                    <label className="section-label">{t('groupAdmin.context.files.title')}</label>
                    <p className="section-description">{t('groupAdmin.context.files.helpText')}</p>

                    {filesLoading ? (
                      <div className="files-loading" style={{ padding: '16px', textAlign: 'center' }}>
                        <div className="spinner" />
                        <span>{t('common.loading')}</span>
                      </div>
                    ) : files.length > 0 && (
                      <div className="bundle-files-list">
                        {files.map((file) => (
                          <div key={file.tenant_attachment_id} className="bundle-file-item">
                            <div
                              className="bundle-file-clickable"
                              onClick={() => setPreviewFile({
                                fileId: file.file_id,
                                fileName: file.attachment_filename,
                                fileType: file.file?.file_type || '',
                              })}
                              title={t('filePreview.preview')}
                            >
                            <div className="bundle-file-icon">
                              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                              </svg>
                            </div>
                            <div className="bundle-file-info">
                              <span dir="auto" className="bundle-file-name">{file.attachment_filename}</span>
                              <span className="bundle-file-meta">
                                <span className="bundle-file-size">{formatFileSize(file.file?.file_size_bytes || 0)}</span>
                              </span>
                            </div>
                            </div>
                            <div className="file-context-toggle">
                              <span className="context-toggle-label">
                                {t('bots.contextFiles.alwaysInContext')}
                              </span>
                              <label className="context-switch">
                                <input
                                  type="checkbox"
                                  checked={file.include_as_context || false}
                                  onChange={() => handleToggleDirectMode(file.tenant_attachment_id, !file.include_as_context)}
                                  disabled={
                                    !onUpdateFile ||
                                    file.context_mode === 'pending' ||
                                    file.context_mode === 'error'
                                  }
                                />
                                <span className="context-switch-slider"></span>
                              </label>
                            </div>
                            <button
                              className="bundle-file-delete"
                              onClick={() => handleDeleteFile(file.tenant_attachment_id)}
                              title={t('common.delete')}
                            >
                              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18" strokeLinecap="round" strokeLinejoin="round" />
                                <line x1="6" y1="6" x2="18" y2="18" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Upload Area */}
                    <UploadArea
                      uploading={uploading}
                      dragOver={dragOver}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onFileSelect={(files) => handleFileUpload(files)}
                      disabled={uploading}
                      accept={KNOWLEDGE_ACCEPT}
                    />

                    {/* The Library as a source beside the computer and the
                        cloud. A tenant is a link subject in its own right —
                        the organization-wide context every agent in it reads —
                        so this writes the same kind of row the agent and skill
                        editors do, with subject_tenant_id. */}
                    {libraryEnabled && selectedTenantId && (
                      <VaultAttachments
                        subject={{ subject_tenant_id: selectedTenantId }}
                        addButtonClassName="btn-cloud-action"
                        previewUrlFor={(fileId) => groupAdminService.tenantContextFileUrl(selectedTenantId, fileId)}
                      />
                    )}

                    {/* Cloud & URL actions */}
                    <div className="bundle-cloud-actions">
                      {onAddCloudFiles && cloudEnabled && (
                        <button
                          type="button"
                          className="btn-cloud-action"
                          onClick={() => setCloudPickerVisible(true)}
                          disabled={uploading}
                        >
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
                          </svg>
                          {t('dataBundles.addFromCloud')}
                        </button>
                      )}
                      {selectedTenantId && (
                        <UrlResearchSection
                          mode="tenant"
                          tenantId={selectedTenantId}
                          onFileSaved={() => loadFiles(selectedTenantId)}
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* Files Context Prompt — auto-syncs on every file
                    change. Read-only. ONE viewer showing the tenant's
                    own column. Same shape as before any of these
                    changes. */}
                {(hasFileSupport || hasBundleSupport) && (
                  <ContextPromptViewer
                    prompt={filesContextPrompt || ''}
                    label={t('bots.modal.filesContextPrompt')}
                    defaultOpen
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* File Preview */}
      <FilePreviewModal
        visible={!!previewFile}
        fileId={previewFile?.fileId}
        fileName={previewFile?.fileName || ''}
        fileType={previewFile?.fileType}
        downloadUrl={previewFile?.fileId && selectedTenantId
          ? groupAdminService.tenantContextFileUrl(selectedTenantId, previewFile.fileId)
          : undefined}
        onClose={() => setPreviewFile(null)}
      />

      {/* Cloud File Picker — only mounted when cloud storage is enabled. */}
      {cloudEnabled && (
        <CloudFilePicker
          visible={cloudPickerVisible}
          onSelect={handleCloudFilesSelected}
          onCancel={() => setCloudPickerVisible(false)}
        />
      )}

      {/* Bundle Selector Modal (Multi-Select — matches BotModal) */}
      {bundleSelectorVisible && (
        <div className="modal-overlay">
          <div className="modal-content bundle-selector-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('bots.modal.selectDataBundles')}</h2>
              {bundles.length > 0 && (
                <span className="bundle-count-badge">{t('common.nSelected', { count: bundles.length })}</span>
              )}
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => { setBundleSelectorVisible(false); setBundleSearchQuery(''); }}
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="skill-search-container">
              <svg className="search-icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input dir="auto"
                type="text"
                className="skill-search-input"
                placeholder={t('bots.modal.searchBundlesPlaceholder')}
                value={bundleSearchQuery}
                onChange={(e) => setBundleSearchQuery(e.target.value)}
                autoFocus
              />
              {bundleSearchQuery && (
                <button
                  type="button"
                  className="clear-search-btn"
                  onClick={() => setBundleSearchQuery('')}
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
            </div>

            <div className="modal-body">
              {loadingAvailableBundles ? (
                <div className="empty-state">
                  <div className="empty-text">{t('dataBundles.loadingBundles')}</div>
                </div>
              ) : filteredAvailableBundles.length === 0 && bundleSearchQuery ? (
                <div className="empty-state">
                  <div className="empty-text">No bundles match "{bundleSearchQuery}"</div>
                </div>
              ) : filteredAvailableBundles.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-text">{t('bots.modal.noDataBundles')}</div>
                  <div className="empty-subtext">{t('bots.modal.noDataBundlesHint')}</div>
                </div>
              ) : (
                <div className="skills-list-vertical">
                  {filteredAvailableBundles.map((bundle) => {
                    const isSelected = bundles.some(b => b.data_bundle_id === bundle.data_bundle_id);
                    return (
                      <div
                        key={bundle.data_bundle_id}
                        className={`skill-list-item ${isSelected ? 'assigned' : ''}`}
                        onClick={() => toggleBundleSelection(bundle.data_bundle_id)}
                      >
                        <div className={`bundle-checkbox ${isSelected ? 'checked' : ''}`}>
                          {isSelected && (
                            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          )}
                        </div>
                        <div className="skill-item-icon">
                          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                          </svg>
                        </div>
                        <div className="skill-item-content">
                          <div className="skill-item-name-row">
                            <span className="skill-item-name" dir="auto">{bundle.bundle_name}</span>
                            {bundle.is_shared && <span className="shared-badge">{t('common.shared')}</span>}
                          </div>
                          <div className="skill-item-description" dir="auto">
                            {bundle.bundle_description || t('counts.files', { count: bundle.file_count })}
                          </div>
                          <div className="skill-item-meta">
                            {bundle.created_by_name && <span>by {bundle.created_by_name}</span>}
                            {bundle.created_at && (
                              <span>{new Date(bundle.created_at).toLocaleDateString(activeLocale())}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-primary"
                onClick={() => { setBundleSelectorVisible(false); setBundleSearchQuery(''); }}
              >
                {t('common.done')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview modal with AgentPreview */}
      <Modal
        title={t('groupAdmin.context.previewTitle')}
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width={800}
      >
        {previewLoading ? (
          <div className="loading-cell" style={{ padding: '40px', textAlign: 'center' }}>
            {t('common.loading')}
          </div>
        ) : (
          <AgentPreview
            title={t('dataBundles.tenantContextPreview')}
            subtitle={t('groupAdmin.tenantContextSubtitle')}
            content={buildPreviewString()}
            defaultCollapsed={false}
            refreshing={refreshingTenantChain}
            onRefresh={onRegenerateContext && selectedTenantId ? async () => {
              // Rebuild this tenant's combined prompt: the regenerate-
              // context endpoint copies each linked bundle's existing
              // bundle_context_prompt verbatim, and Haiku-summarizes each
              // of THIS tenant's direct files. Bundles are NOT re-Haiku'd
              // here — that's the bundle's own refresh button.
              try {
                setRefreshingTenantChain(true);
                await onRegenerateContext(selectedTenantId);
                // Refresh the local viewer with the latest tenant prompt
                const refreshed = await onGetContextPrompt(selectedTenantId);
                setFilesContextPrompt(refreshed.files_context_prompt || '');
                // Rebuild the preview drawer content (mirrors `loadPreview`)
                const preview = await onPreviewContext(selectedTenantId);
                setPreviewContent(preview.hierarchical_context || t('groupAdmin.context.noContext'));
                setPreviewFiles(preview.files || []);
                setPreviewFilesPrompt(preview.files_context_prompt || '');
              } catch (err) {
                console.error('[TenantContextEditor] refresh failed', err);
              } finally {
                setRefreshingTenantChain(false);
              }
            } : undefined}
          />
        )}
      </Modal>
    </div>
  );
};

export default TenantContextEditor;
